import React from 'react';
import { generatePath, Link, matchPath } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import useMediaQuery from '@mui/material/useMediaQuery';
import mediaQueries from '../../mediaqueries';
import { useLogout } from 'ducks/user';
import { useUserContext } from 'common/contexts/UserContext';
import { useNavHeightContext } from 'common/contexts/NavHeightContext';
import { ContainerTracking } from 'components/ContainerTracking';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSegment } from 'common/hooks/segment';
import { useRegion } from 'common/hooks/region';

const SIDEBAR_WIDTH = 240;

const MOBILE_HEIGHT = 50;

const USER_GUIDE_URL =
  'https://long-beach-us.s3.us-west-1.amazonaws.com/user_guide/BlueCargo+Onboarding+Guide+-+June+22.pdf';

const SideNavigationLayout = ({
  children,
  currentPath,
  sideNavigationContent,
  isLogout = false,
  showUserGuide,
}) => {
  const tablet = useMediaQuery(`(max-width:${mediaQueries.md})`);
  const { user } = useUserContext();
  const region = useRegion();
  const handleLogout = useLogout();

  if (!tablet) {
    return (
      <SideNavigationDesktop
        children={children}
        currentPath={currentPath}
        sideNavigationContent={sideNavigationContent}
        isLogout={isLogout}
        handleLogout={handleLogout}
        user={user}
        showUserGuide={showUserGuide}
        region={region}
      />
    );
  } else {
    return (
      <TopNavigationMobile
        children={children}
        currentPath={currentPath}
        sideNavigationContent={sideNavigationContent}
        isLogout={isLogout}
        handleLogout={handleLogout}
        user={user}
        showUserGuide={showUserGuide}
        region={region}
      />
    );
  }
};

const SideNavigationDesktop = ({
  children,
  currentPath,
  sideNavigationContent,
  user,
  showUserGuide,
  region,
}) => {
  const { trackEventNoSlack } = useSegment();
  const { marginTop } = useNavHeightContext();

  return (
    <main className="flex w-full">
      {/* sidebar */}
      <div
        style={{
          width: `${SIDEBAR_WIDTH}px`,
          height: `calc(100vh - ${marginTop}px)`,
        }}
        className="fixed flex flex-col bg-white border-r border-blue-200 pt-4 pb-8"
      >
        {sideNavigationContent.map((nav, index) => {
          return (
            <Accordion
              disableGutters
              elevation={0}
              defaultExpanded={index === 0}
              key={`p-${index}`}
              sx={[
                () => ({
                  '&:before': {
                    backgroundColor: 'white',
                  },
                }),
              ]}
            >
              <AccordionSummary
                // expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                sx={{ margin: 0 }}
              >
                <div className="">
                  <h4 className="font-bold">{nav.title}</h4>
                </div>{' '}
              </AccordionSummary>
              <AccordionDetails>
                <div className="border-l border-blue-200 pl-3">
                  <ul className="flex flex-col space-y-4">
                    {nav.children &&
                      nav.children.map((item, idx) => {
                        const active = matchPath(item.page.path, currentPath);
                        return (
                          <li key={idx} className="flex">
                            <hr
                              className={` ${active && 'absolute left-4 h-5 w-0.5 bg-blue-900'}`}
                            ></hr>
                            <Link to={generatePath(item.page.path, { region: region.route })}>
                              <h4
                                className={`capitalize ${
                                  active ? 'font-bold text-blue-900' : 'font-regular text-gray-900'
                                }`}
                              >
                                {item.page.title}
                              </h4>
                            </Link>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              </AccordionDetails>
            </Accordion>
          );
        })}
        <div className="w-full absolute bottom-0">
          {showUserGuide && (
            <div className="flex space-x-1 p-4 items-center hover:text-blue-900">
              <HelpOutlineIcon style={{ fontSize: 20 }} className="text-gray-400" />
              <a
                href={USER_GUIDE_URL}
                target="_blank"
                rel="noreferrer"
                className="hover:underline hover:text-blue-900"
                onClick={() => {
                  trackEventNoSlack(`User Guide Clicked`, {});
                }}
              >
                User Guide
              </a>
            </div>
          )}
          <div className="bg-blue-100 border-t border-blue-200 pt-4 pb-8 px-4">
            {user.company && <ContainerTracking />}
          </div>
        </div>
      </div>
      <div className="w-full h-full bg-blue-50" style={{ marginLeft: `${SIDEBAR_WIDTH}px` }}>
        {children}
      </div>
    </main>
  );
};

const TopNavigationMobile = ({
  children,
  currentPath,
  sideNavigationContent,
  user,
  showUserGuide,
  region,
}) => {
  const { trackEventNoSlack } = useSegment();

  return (
    <main>
      <div
        className="overflow-auto whitespace-nowrap w-full px-8 bg-white border-b border-blue-200"
        style={{ height: `${MOBILE_HEIGHT}px` }}
      >
        {/* content */}
        <ul className="h-full w-full flex items-center space-x-4">
          {sideNavigationContent &&
            sideNavigationContent.map((nav, index) => {
              return (
                <ul key={nav.title} className="w-full flex space-x-8 items-center">
                  {nav.children &&
                    nav.children.map((item, idx) => {
                      const active = matchPath(item.page.path, currentPath);
                      return (
                        <li key={idx}>
                          <Link to={generatePath(item.page.path, { region: region.route })}>
                            <h4
                              className={`capitalize ${
                                active ? 'font-bold text-blue-900' : 'font-regular text-gray-900'
                              }`}
                            >
                              {item.page.title}
                            </h4>
                          </Link>
                        </li>
                      );
                    })}
                  {showUserGuide && (
                    <li>
                      <div className="flex space-x-1 p-4 items-center hover:text-blue-900">
                        <HelpOutlineIcon style={{ fontSize: 20 }} className="text-gray-400" />
                        <a
                          href={USER_GUIDE_URL}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline hover:text-blue-900"
                          onClick={() => {
                            trackEventNoSlack(`User Guide Clicked`, {});
                          }}
                        >
                          User Guide
                        </a>
                      </div>
                    </li>
                  )}
                </ul>
              );
            })}
        </ul>
      </div>
      <div className="w-full bg-blue-50">{children}</div>
    </main>
  );
};

export default SideNavigationLayout;
